import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { GitBranch, FlaskConical, Check, X } from 'lucide-react';
import { axios, auth, mbUrl, REVIEW_LABEL, NO_PERM, fmtTotals, cpsClass, muted, primary, runCall } from './mbApi';
import { ArchiveToggle } from './ArchiveToggle';
import { TestRow } from './TestRow';
import { LearningPanel } from './LearningPanel';
import { MetadataEditor } from './MetadataEditor';
import { AnalystPanel } from './AnalystPanel';
import { AssetPreview, OpenAsset } from './AssetPreview';

const REVIEW_CLS = { lulus: 'bg-emerald-500/15 text-emerald-400', perlu_ubah: 'bg-red-500/15 text-red-400', belum_disemak: 'bg-white/5' };
const HISTORY_LABEL = { ...REVIEW_LABEL, dibatalkan: 'Kelulusan dibatalkan' };

const VariationForm = ({ ibId, c, onDone }) => {
  const [f, setF] = useState({ version_label: '', changes_summary: '', hook: c.hook || '', asset_link: '' });
  const [busy, setBusy] = useState(false);
  const submit = () => runCall(setBusy, async () => {
    await axios.post(mbUrl(ibId, `/creatives/${c.id}/variation`), f, auth());
    onDone();
  }, 'Variasi created');
  return (
    <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`mb-variation-form-${c.id}`}>
      <Input className="h-9 text-xs w-36" placeholder="Label baru (V2…)" value={f.version_label} onChange={(e) => setF({ ...f, version_label: e.target.value })} data-testid={`mb-variation-label-${c.id}`} />
      <Input className="h-9 text-xs w-56" placeholder="Apa yang berubah?" value={f.changes_summary} onChange={(e) => setF({ ...f, changes_summary: e.target.value })} data-testid={`mb-variation-changes-${c.id}`} />
      <Input className="h-9 text-xs w-32" placeholder="Hook" value={f.hook} onChange={(e) => setF({ ...f, hook: e.target.value })} data-testid={`mb-variation-hook-${c.id}`} />
      <Input className="h-9 text-xs w-48" placeholder="Link bahan baru (optional)" value={f.asset_link} onChange={(e) => setF({ ...f, asset_link: e.target.value })} data-testid={`mb-variation-asset-${c.id}`} />
      <Button size="sm" variant="outline" className="h-9 text-xs" disabled={busy || !f.version_label.trim() || !f.changes_summary.trim()} onClick={submit} data-testid={`mb-variation-submit-${c.id}`}>Create</Button>
    </div>
  );
};

const RejectForm = ({ c, busy, onSubmit, onCancel }) => {
  const [note, setNote] = useState('');
  return (
    <div className="flex flex-wrap gap-1.5 w-full mt-1" data-testid={`mb-review-reject-form-${c.id}`}>
      <Input className="h-10 text-xs flex-1 min-w-[200px]" maxLength={500} placeholder="Apa yang perlu diubah? (wajib, contoh: CTA kurang jelas, tukar hook)"
        value={note} onChange={(e) => setNote(e.target.value)} data-testid={`mb-review-note-${c.id}`} />
      <Button size="sm" variant="outline" className="h-10 text-xs" disabled={busy || !note.trim()} onClick={() => onSubmit(note.trim())} data-testid={`mb-review-reject-submit-${c.id}`}>Hantar Kena ubah</Button>
      <Button size="sm" variant="ghost" className="h-10 text-xs" onClick={onCancel} data-testid={`mb-review-reject-cancel-${c.id}`}>Batal</Button>
    </div>
  );
};

const ReviewNote = ({ c }) => {
  if (c.review_status !== 'perlu_ubah') return null;
  const last = (c.review_history || []).slice(-1)[0];
  const active = last?.status === 'perlu_ubah' ? last.note : c.review_note;
  return (
    <p className="text-[11px] text-red-400 w-full" data-testid={`mb-review-note-display-${c.id}`}>
      {active ? `Perlu ubah: "${active}"` : 'Nota perubahan belum direkod'}
    </p>
  );
};

const ReviewHistory = ({ c }) => (c.review_history || []).length > 0 && (
  <details className={`text-[10px] ${muted} w-full`}>
    <summary className="cursor-pointer" data-testid={`mb-review-history-${c.id}`}>Sejarah review ({c.review_history.length})</summary>
    {[...c.review_history].reverse().map((h, i) => (
      <p key={`${h.at}-${i}`}>{new Date(h.at).toLocaleString('en-GB')} · {h.by_name}: {HISTORY_LABEL[h.status] || h.status}{h.note ? ` — "${h.note}"` : ''}</p>
    ))}
  </details>
);

const ReviewBar = ({ ibId, c, canWrite, reload }) => {
  const [busy, setBusy] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const review = (review_status, review_note) => runCall(setBusy, async () => {
    await axios.put(mbUrl(ibId, `/creatives/${c.id}/review`), { review_status, ...(review_note ? { review_note } : {}) }, auth());
    setRejecting(false);
    reload();
  }, review_status === 'lulus' ? 'Bahan lulus review' : 'Ditanda kena ubah');
  return (
    <>
      <span className="flex items-center gap-1">
        <span className={`px-1.5 py-0.5 rounded text-[10px] ${REVIEW_CLS[c.review_status]}`} data-testid={`mb-review-status-${c.id}`}>
          {REVIEW_LABEL[c.review_status]}{c.reviewed_by ? ` · ${c.reviewed_by}` : ''}
        </span>
        {canWrite && c.review_status !== 'lulus' && (
          <button disabled={busy} onClick={() => review('lulus')} className="min-w-10 min-h-10 inline-flex items-center justify-center rounded hover:text-emerald-400 hover:bg-white/5" title="Lulus (mesej, visual, CTA OK)" aria-label={`Lulus review ${c.version_label}`} data-testid={`mb-review-approve-${c.id}`}><Check size={18} /></button>
        )}
        {canWrite && c.review_status !== 'perlu_ubah' && (
          <button disabled={busy} onClick={() => setRejecting(true)} className="min-w-10 min-h-10 inline-flex items-center justify-center rounded hover:text-red-400 hover:bg-white/5" title="Kena ubah (wajib tulis sebab)" aria-label={`Kena ubah ${c.version_label}`} data-testid={`mb-review-reject-${c.id}`}><X size={18} /></button>
        )}
      </span>
      {rejecting && <RejectForm c={c} busy={busy} onSubmit={(n) => review('perlu_ubah', n)} onCancel={() => setRejecting(false)} />}
    </>
  );
};

const Head = ({ ibId, c, parentLabel, canWrite, canManage, section, reload }) => (
  <div className="flex flex-wrap items-start gap-3">
    <AssetPreview c={c} small={section !== 'bahan'} />
    <div className="min-w-0 flex-1 space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`text-sm font-semibold ${primary}`}>{c.version_label}</span>
        <ReviewBar ibId={ibId} c={c} canWrite={canWrite && section === 'bahan'} reload={reload} />
        {c.archived && <span className="px-1.5 rounded bg-white/10 text-xs" data-testid={`mb-creative-archived-${c.id}`}>Arkib</span>}
        {section === 'bahan' && ((canWrite && !c.archived) || (canManage && c.archived)) && (
          <ArchiveToggle ibId={ibId} kind="creatives" rec={c} label={`versi ${c.version_label}`} reload={reload} testid={`mb-creative-archive-${c.id}`} className={muted} />
        )}
      </div>
      {(c.format || c.angle) && <p className={`text-sm ${muted} break-words`}>{[c.format, c.angle].filter(Boolean).join(' · ')}</p>}
      {c.hook && <p className="text-sm break-words"><span className={muted}>Hook:</span> <span className={primary}>{c.hook}</span></p>}
      <p className={`text-sm break-words ${parentLabel || c.changes_summary ? 'text-sky-400' : muted}`} data-testid={`mb-creative-changes-${c.id}`}>
        Unsur berubah: {c.changes_summary || (parentLabel ? '—' : 'versi asal (tiada perubahan direkod)')}{parentLabel ? ` (variasi dari ${parentLabel})` : ''}
      </p>
      {section === 'bahan' && <OpenAsset c={c} />}
    </div>
  </div>
);

const Tests = ({ ibId, c, tests, results, versionTotals, canTest, canManage, stageOk, reload, simulate }) => {
  const [busy, setBusy] = useState(false);
  const keyRef = useRef(null);
  const blockedWhy = c.review_status !== 'lulus' ? 'bahan kena lulus review dulu'
    : !stageOk ? 'task belum diserahkan ke tahap Ujian (Firzan → Azrai, di tab Ringkasan)' : '';
  const newTest = () => runCall(setBusy, async () => {
    keyRef.current = keyRef.current || crypto.randomUUID();
    await axios.post(mbUrl(ibId, '/tests'), { creative_id: c.id, client_key: keyRef.current }, auth());
    keyRef.current = null;
    reload();
  }, 'Ujian baru dicipta');
  return (
    <>
      <p className={`text-sm ${muted}`} data-testid={`mb-version-result-${c.id}`}>
        Total versi ni: spend {fmtTotals(versionTotals?.totals)} · {versionTotals?.joins || 0} semua join · CPS semua join <span className={cpsClass(versionTotals?.cps_myr)}>{versionTotals?.cps_myr != null ? `RM ${versionTotals.cps_myr.toFixed(2)}` : 'N/A'}</span>
        {' · '}{tests.length} ujian{versionTotals?.delivering ? ' · menerima delivery' : ''}
      </p>
      {!canTest && !c.archived && <p className={`text-xs ${muted}`}>Ujian: {NO_PERM.test}</p>}
      {tests.map((t) => <TestRow key={t.id} ibId={ibId} t={t} result={results[t.id]} canWrite={canTest} canManage={canManage} reload={reload} simulate={simulate} />)}
      {canTest && !c.archived && (
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" className="h-10" disabled={busy || c.review_status !== 'lulus' || !stageOk} onClick={newTest}
            title={blockedWhy} data-testid={`mb-test-add-${c.id}`}>
            <FlaskConical size={14} className="mr-1" /> Ujian Baru
          </Button>
          {blockedWhy && <span className={`text-xs ${muted}`} data-testid={`mb-test-add-why-${c.id}`}>Ujian Baru: {blockedWhy}</span>}
        </div>
      )}
    </>
  );
};

export const VersionRow = (props) => {
  const { ibId, c, canCreative, canLearn, canBrief, suggestionId, learnings = [], reload, section = 'bahan' } = props;
  const canWrite = canCreative;
  const [showVar, setShowVar] = useState(false);
  return (
    <div className="rounded-lg border border-[hsl(var(--border))] p-3 space-y-2 scroll-mt-28 transition-shadow" data-testid={`mb-creative-${c.id}`}>
      <Head {...props} canWrite={canWrite} section={section} />
      {section === 'bahan' && (
        <>
          <ReviewNote c={c} />
          <ReviewHistory c={c} />
          <MetadataEditor ibId={ibId} c={c} canWrite={canWrite && !c.archived} reload={reload} />
          {canWrite && !c.archived && (
            <Button size="sm" variant="outline" className="h-10" onClick={() => setShowVar(!showVar)} data-testid={`mb-variation-toggle-${c.id}`}>
              <GitBranch size={14} className="mr-1" /> Buat Variasi
            </Button>
          )}
          {showVar && <VariationForm ibId={ibId} c={c} onDone={() => { setShowVar(false); reload(); }} />}
        </>
      )}
      {section === 'ujian' && <Tests {...props} />}
      {section === 'belajar' && (
        <>
          <LearningPanel ibId={ibId} c={c} learnings={learnings} canWrite={canLearn && !c.archived} suggestionId={suggestionId} reload={reload} />
          {!c.archived && <AnalystPanel ibId={ibId} c={c} canRun={canLearn} canBrief={canBrief} />}
        </>
      )}
    </div>
  );
};
